import {
  Card,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { ProjectStatsQueryDTO } from "@/modules/management/project/domain/repo/get-project-stats";
import type {
  ListProjectsQuery,
  ProjectStatus,
} from "@/modules/management/project/domain/types";
import { managementProjectApi } from "../api/client";

const LIST_LIMIT = 50;

type ProjectSummaryStatPopoverCardProps = {
  statsQuery: ProjectStatsQueryDTO;
  /** ຖ້າບໍ່ສົ່ງ = ລາຍການທັງໝົດຕາມ filter (ບໍ່ກອງ status) */
  status?: ProjectStatus;
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  iconWrapClassName: string;
  cardClassName: string;
  labelClassName?: string;
  valueClassName?: string;
};

export function ProjectSummaryStatPopoverCard({
  statsQuery,
  status,
  label,
  value,
  icon,
  iconWrapClassName,
  cardClassName,
  labelClassName = "text-muted-foreground text-sm",
  valueClassName = "font-bold text-2xl",
}: ProjectSummaryStatPopoverCardProps) {
  const [open, setOpen] = useState(false);

  const listQuery: ListProjectsQuery = useMemo(
    () => ({
      limit: LIST_LIMIT,
      offset: 0,
      departmentId: statsQuery.departmentId,
      startDateFrom: statsQuery.startDateFrom,
      startDateTo: statsQuery.startDateTo,
      level: statsQuery.level,
      ...(status ? { status } : {}),
    }),
    [
      statsQuery.departmentId,
      statsQuery.startDateFrom,
      statsQuery.startDateTo,
      statsQuery.level,
      status,
    ],
  );

  const { data, isPending, isFetching } = useQuery({
    queryKey: [
      "management",
      "projects",
      "list",
      "summary-stat-popover",
      listQuery,
    ],
    queryFn: () => managementProjectApi.list(listQuery),
    enabled: open,
    staleTime: 30_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="block w-full rounded-xl p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={`${label} — ເບິ່ງລາຍການໂຄງການ`}
        >
          <Card
            className={`flex flex-col gap-0 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${cardClassName}`}
          >
            <div className="flex items-center justify-between gap-0">
              <div className="flex min-w-0 flex-col gap-0">
                <p className={labelClassName}>{label}</p>
                <p className={valueClassName}>{value}</p>
              </div>
              <div className={`shrink-0 ${iconWrapClassName}`}>{icon}</div>
            </div>
          </Card>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(100vw-2rem,22rem)] p-0"
        align="start"
        sideOffset={8}
      >
        <div className="border-border/60 border-b px-3 py-2">
          <p className="font-medium text-sm">{label}</p>
          <p className="text-muted-foreground text-xs">
            ເລືອກໂຄງການເພື່ອເປີດລາຍລະອຽດ
          </p>
        </div>
        <ScrollArea className="max-h-[min(50vh,280px)]">
          {isPending || isFetching ? (
            <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              ກໍາລັງໂຫຼດ...
            </div>
          ) : !data?.data.length ? (
            <p className="px-3 py-8 text-center text-muted-foreground text-sm">
              ບໍ່ມີໂຄງການ
            </p>
          ) : (
            <ul className="py-1">
              {data.data.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/app/management/projects/$id"
                    params={{ id: p.id }}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm transition-colors hover:bg-muted/80"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {p.name}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
        {data && data.meta.total > LIST_LIMIT ? (
          <p className="border-border/60 border-t px-3 py-2 text-muted-foreground text-xs">
            ສະແດງ {LIST_LIMIT} ລາຍການທຳອິດ ຈາກທັງໝົດ {data.meta.total}
          </p>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
